import {
  type AllHTMLAttributes,
  type ChangeEvent,
  type JSX,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import Box from '@common/components/Box';
import Button from '@common/components/controls/Button';
import { FILE_UPLOAD_ANIMATION_DELAY_MS } from '@common/components/controls/constants';
import IconButton from '@common/components/controls/IconButton';
import { type RequiredTestId } from '@common/components/controls/TestId';
import SimpleList from '@common/components/SimpleList';
import SimpleListItem from '@common/components/SimpleList/SimpleListItem';
import IconAttachment from '@common/components/svgs/common/Attachment';
import IconRecycleBin from '@common/components/svgs/common/RecycleBin';
import Translated from '@common/components/Translated';
import { isFileAllowed, isFileSizeValid } from '@common/utils/validation/validate';
import '@common/styles/components/fileUpload.pcss';

const formatBytes = (bytes: number, decimals = 2) => {
  if (bytes === 0) { return '0 Bytes'; }

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / k ** i).toFixed(dm))} ${sizes[i]}`;
};

const getFileData = (
  file: File,
  index: number,
  accept?: string,
  size?: number,
) => {
  let errorMessage: undefined | JSX.Element;
  if (accept && !isFileAllowed(file, accept)) {
    errorMessage = <Translated id="fileUpload.errors.type" />;
  } else if (size && !isFileSizeValid(file, size)) {
    errorMessage = (
      <Translated id="fileUpload.errors.size" values={{ allowedSize: formatBytes(size) }} />
    );
  }
  return (
    <span>
      <div className="fileUpload__label">
        <Translated id="fileUpload.attached-file" values={{ number: index + 1 }} />
      </div>
      <div className="fileUpload__filename">
        {file.name}
      </div>
      {errorMessage && (
        <Box className="txt-error">
          {errorMessage}
        </Box>
      )}
    </span>
  );
};

interface FileUploadItemProps {
  id: string;
  file: File;
  index: number;
  accept: FileUploadProps['accept'];
  size: FileUploadProps['size'];
  disabled: FileUploadProps['disabled'];
  onDelete: (fileName: string, secondId: number) => void;
}

function FileUploadItem({
  id,
  file,
  index,
  accept,
  size,
  disabled,
  onDelete,
}: Readonly<FileUploadItemProps>) {
  const [deleting, setDeleting] = useState(false);
  const deleteTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup timeout on unmount to prevent memory leaks and state updates on unmounted component
  useEffect(() => {
    return () => {
      if (deleteTimeoutRef.current) {
        clearTimeout(deleteTimeoutRef.current);
        deleteTimeoutRef.current = null;
      }
    };
  }, []);

  const onDeleteItem = useCallback(
    () => {
      // Clear any pending delete animation to prevent multiple concurrent deletes
      if (deleteTimeoutRef.current) {
        clearTimeout(deleteTimeoutRef.current);
      }

      setDeleting(true);
      deleteTimeoutRef.current = setTimeout(() => {
        setDeleting(false);
        onDelete(file.name, index);
        deleteTimeoutRef.current = null;
      }, FILE_UPLOAD_ANIMATION_DELAY_MS);
    },
    [file.name, index, onDelete],
  );
  return (
    <SimpleListItem id={id} deleting={deleting}>
      {getFileData(file, index, accept, size)}
      <span className="list__option-last">
        <IconButton
          data-testid={`${file.name}_deleteButton`}
          icon={IconRecycleBin}
          color="regular"
          onClick={onDeleteItem}
          disabled={disabled}
          aria-label={`Remove ${file.name}`}
        />
      </span>
    </SimpleListItem>
  );
}

export interface FileUploadProps
  extends Omit<AllHTMLAttributes<HTMLInputElement>, 'defaultValue' | 'type' | 'value'>
{
  name: string;
  value: File[];
  onDelete: FileUploadItemProps['onDelete'];
  loading?: boolean;
  showError?: boolean;
  touched?: boolean;
  error?: string;
}

function FileUpload({
  multiple,
  name,
  value,
  onDelete,
  size,
  disabled = false,
  loading,
  accept,
  showError,
  error,
  touched,
  'data-testid': testId,
  onChange,
  ...rest
}: Readonly<RequiredTestId<FileUploadProps>>) {
  const inputRef = useRef<HTMLInputElement | null>(null);

  const openFileDialog = () => {
    inputRef.current?.click();
  };

  // Reset file input when parent clears the value (programmatic reset)
  // This allows forms to clear file selection via setting value={[]}
  useEffect(() => {
    if (value.length === 0 && inputRef.current) {
      inputRef.current.value = '';
    }
  }, [value.length]);

  // Handle file selection and pass files to parent via onChange
  const handleFileChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    if (onChange && event.target.files) {
      // Call parent onChange handler with the file change event
      onChange(event);

      // Reset input value to allow selecting the same file again
      event.target.value = '';
    }
  }, [onChange]);

  return (
    <div className="fileUpload">
      <input
        className="fileUpload__input"
        name={name}
        aria-hidden="true"
        tabIndex={-1}
      />
      <input
        ref={inputRef}
        type="file"
        className="fileUpload__input"
        multiple={multiple}
        accept={accept}
        disabled={disabled}
        onChange={handleFileChange}
        value=""
        aria-label={multiple ? 'Select files to upload' : 'Select file to upload'}
        data-testid={testId}
        {...rest}
      />
      {value && value.length > 0 && (
        <div className="fileUpload__list">
          <SimpleList>
            {value.map((file: File, index) => {
              const id = `${file.name}::${index}`;
              return (
                <FileUploadItem
                  key={id}
                  id={id}
                  file={file}
                  index={index}
                  accept={accept}
                  size={size}
                  disabled={disabled}
                  onDelete={onDelete}
                />
              );
            })}
          </SimpleList>
        </div>
      )}
      <Button
        mode="text"
        type="button"
        leadingIconComponent={IconAttachment}
        leadingIconSize={16}
        loadingState={loading ? 'loading' : undefined}
        onClick={openFileDialog}
        disabled={disabled}
        aria-label={!multiple && value && value.length > 0
          ? 'Replace file'
          : (multiple ? 'Select files to upload' : 'Select file to upload')}
        data-testid="upload-button"
      >
        {!multiple && value && value.length > 0
          ? <Translated id="common.actions.replace-file" />
          : (
            <Translated
              id={multiple ? 'common.actions.upload-files' : 'common.actions.upload-file'}
            />
          )}
      </Button>
      {showError && touched && error && (
        <p className="txt-small txt-error">
          <Translated id={error} />
        </p>
      )}
    </div>
  );
}

export default FileUpload;
